/**
 * WKS Variant Image Fallback 1.4
 * Weihnachtskrippenshop
 * 24.09.2026
 *
 * GRUNDLOGIK
 * ---------------------------------------------------------
 * Pro Ausführung existiert maximal ein relevantes Produktbild.
 * Die Größe spielt für die Bildauswahl KEINE Rolle.
 *
 * 1. Gewählte Ausführung besitzt irgendwo ein Bild:
 *    -> dieses Bild anzeigen
 *    -> kein Hinweis
 *
 * 2. Gewählte Ausführung besitzt überhaupt kein Bild:
 *    -> Ersatzbild verwenden
 *    -> Priorität: Natur -> Bemalt -> Color -> Lasiert
 *    -> Hinweis anzeigen
 *
 * 3. Kein geeignetes Ersatzbild vorhanden:
 *    -> generisches WKS-Fallbackbild
 *
 * WICHTIG 1.2
 * ---------------------------------------------------------
 * - Prestige bleibt Eigentümer der Produktgalerie.
 * - Keine dauerhafte Manipulation von hidden-Zuständen.
 * - Kein scrollIntoView().
 * - Kein Eingriff in den vertikalen Seiten-Scroll.
 * - Kein document.body MutationObserver.
 * - Kein künstliches Verstecken/Wiederherstellen von Medien.
 * - Variantenwahl muss jederzeit bedienbar bleiben.
 * - Mobile Swipe-/Galerielogik von Prestige bleibt erhalten.
 */
(() => {
  'use strict';
  const VERSION = '1.5';
  const GENERIC_FALLBACK_IMAGE =
    'https://cdn.shopify.com/s/files/1/1060/9478/8938/files/Bild_Ausfuehrung_nicht_verfuegbar_ergebnis.webp?v=1789760985';
  const FALLBACK_EXECUTIONS = [
    'natur',
    'bemalt',
    'color',
    'lasiert'
  ];
  const NOTICE_TITLE =
    'Abbildung für diese Ausführung derzeit nicht verfügbar.';
  const NOTICE_TEXT =
    'Die Ausführung kann vom dargestellten Produktbild abweichen.';
  /* =======================================================
     Hilfsfunktionen
  ======================================================= */
  const normalize = (value) => {
    return String(value ?? '')
      .trim()
      .toLocaleLowerCase('de-DE')
      .replace(/\s+/g, ' ');
  };
  const parseJSON = (value, fallback = null) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };
  const escapeSelector = (value) => {
    if (
      window.CSS &&
      typeof window.CSS.escape === 'function'
    ) {
      return window.CSS.escape(String(value));
    }
    return String(value).replace(/["\\\\]/g, '\\\\$&');
  };
  /* =======================================================
     Konfiguration / Produktdaten
  ======================================================= */
  const getConfigElements = () => {
    return Array.from(
      document.querySelectorAll(
        '[data-wks-variant-image-fallback]'
      )
    );
  };
  const getForm = (formId) => {
    if (!formId) return null;
    return (
      document.getElementById(formId) ||
      document.querySelector(
        `form[id="${escapeSelector(formId)}"]`
      )
    );
  };
  const getSelectedVariantId = (form) => {
    if (!form) return null;
    const variantInput =
      form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) {
      return null;
    }
    return String(variantInput.value);
  };
  const getVariantPicker = (config) => {
    const formId = config.dataset.wksFormId;
    if (!formId) return null;
    return document.querySelector(
      `variant-picker[form-id="${escapeSelector(formId)}"]`
    );
  };
  const getVariantFromPicker = (config) => {
    const picker = getVariantPicker(config);
    if (!picker) return null;
    const script =
      picker.querySelector('script[data-variant]');
    if (!script) return null;
    return parseJSON(script.textContent, null);
  };
  const getVariantData = (config) => {
    const script = config.querySelector(
      'script[data-wks-variant-image-data]'
    );
    if (!script) return null;
    return parseJSON(script.textContent, null);
  };
  const findVariant = (data, variantId) => {
    if (
      !data ||
      !Array.isArray(data.variants) ||
      !variantId
    ) {
      return null;
    }
    return (
      data.variants.find(
        (variant) =>
          String(variant.id) === String(variantId)
      ) || null
    );
  };
  const getCurrentVariant = (config, data) => {
    const pickerVariant =
      getVariantFromPicker(config);
    if (pickerVariant?.id) {
      const fullVariant =
        findVariant(data, pickerVariant.id);
      if (fullVariant) {
        return fullVariant;
      }
    }
    const form =
      getForm(config.dataset.wksFormId);
    const variantId =
      getSelectedVariantId(form);
    return findVariant(data, variantId);
  };
  /* =======================================================
     Ausführung
  ======================================================= */
  const getExecutionValue = (variant, data) => {
    if (!variant || !data) return '';
    const position =
      Number(data.executionOptionPosition);
    if (
      !position ||
      !Array.isArray(variant.options)
    ) {
      return '';
    }
    return variant.options[position - 1] || '';
  };
  const hasOwnMedia = (variant) => {
    return Boolean(
      variant &&
      variant.featuredMedia &&
      variant.featuredMedia.id &&
      variant.featuredMedia.previewImage
    );
  };
    /* =======================================================
     Einzelbild-Sonderregel
  ======================================================= */
  const getRealProductMedia = (data) => {
    if (!data || !Array.isArray(data.variants)) return [];
    const mediaById = new Map();
    data.variants.forEach((variant) => {
      if (!hasOwnMedia(variant)) return;
      const media = variant.featuredMedia;
      const mediaId = String(media.id);
      if (!mediaById.has(mediaId)) mediaById.set(mediaId, media);
    });
    return Array.from(mediaById.values());
  };
  const getSingleProductMedia = (data) => {
    const media = getRealProductMedia(data);
    return media.length === 1 ? media[0] : null;
  };
/**
   * Sucht ein Bild für die aktuell gewählte AUSFÜHRUNG.
   *
   * Die Größe wird vollständig ignoriert.
   *
   * Beispiel:
   *
   * 10 cm / Bemalt -> Bild
   * 12 cm / Bemalt -> kein eigenes Variant-Medium
   *
   * Ergebnis:
   * Das vorhandene Bemalt-Bild wird verwendet.
   */
  const findExecutionVariant = (
    data,
    currentVariant
  ) => {
    if (
      !data ||
      !currentVariant ||
      !Array.isArray(data.variants)
    ) {
      return null;
    }
    const currentExecution = normalize(
      getExecutionValue(currentVariant, data)
    );
    if (!currentExecution) {
      return null;
    }
    return (
      data.variants.find((variant) => {
        if (!hasOwnMedia(variant)) {
          return false;
        }
        const variantExecution = normalize(
          getExecutionValue(variant, data)
        );
        return (
          variantExecution === currentExecution
        );
      }) || null
    );
  };
  /**
   * Ersatz-Ausführung.
   *
   * Nur wenn für die tatsächlich gewählte
   * Ausführung überhaupt kein Bild existiert.
   *
   * Exakte Übereinstimmung:
   * "bemalt" trifft NICHT "wasserfarben bemalt".
   */
  const findFallbackVariant = (data) => {
    if (
      !data ||
      !Array.isArray(data.variants) ||
      !data.executionOptionPosition
    ) {
      return null;
    }
    for (
      const execution of FALLBACK_EXECUTIONS
    ) {
      const fallbackVariant =
        data.variants.find((variant) => {
          if (!hasOwnMedia(variant)) {
            return false;
          }
          return (
            normalize(
              getExecutionValue(variant, data)
            ) === normalize(execution)
          );
        });
      if (fallbackVariant) {
        return fallbackVariant;
      }
    }
    return null;
  };
  /* =======================================================
     Prestige Galerie
  ======================================================= */
  const getGallery = (config) => {
    const section =
      config.closest('.shopify-section');
    if (!section) return null;
    return section.querySelector(
      'product-gallery'
    );
  };
  const getGalleryCarousel = (gallery) => {
    return (
      gallery?.querySelector(
        '.product-gallery__carousel'
      ) || null
    );
  };
  const getMediaElement = (
    gallery,
    mediaId
  ) => {
    if (!gallery || !mediaId) {
      return null;
    }
    return gallery.querySelector(
      `.product-gallery__media[data-media-id="${escapeSelector(
        mediaId
      )}"]`
    );
  };
  const getThumbnailElement = (
    gallery,
    mediaId
  ) => {
    if (!gallery || !mediaId) {
      return null;
    }
    return gallery.querySelector(
      `.product-gallery__thumbnail[data-media-id="${escapeSelector(
        mediaId
      )}"]`
    );
  };
  /* =======================================================
     WKS Elemente entfernen
  ======================================================= */
  const removeNotice = (gallery) => {
    if (!gallery) return;
    gallery
      .querySelectorAll(
        '[data-wks-image-fallback-notice]'
      )
      .forEach((element) => {
        element.remove();
      });
  };
  const removeGenericFallback = (gallery) => {
    if (!gallery) return;
    gallery
      .querySelectorAll(
        '[data-wks-generic-fallback]'
      )
      .forEach((element) => {
        element.remove();
      });
  };
  /**
   * WICHTIG:
   *
   * Anders als 1.1 verändern wir hier KEINE
   * hidden-Eigenschaften von Prestige-Medien.
   */
  const resetWksState = (gallery) => {
    if (!gallery) return;
    removeNotice(gallery);
    removeGenericFallback(gallery);
    gallery.removeAttribute(
      'data-wks-fallback-active'
    );
    gallery.removeAttribute(
      'data-wks-fallback-type'
    );
    gallery.removeAttribute(
      'data-wks-fallback-media-id'
    );
  };
  /* =======================================================
     Hinweis
  ======================================================= */
  const createNotice = () => {
    const notice =
      document.createElement('div');
    notice.className =
      'wks-variant-image-notice';
    notice.setAttribute(
      'data-wks-image-fallback-notice',
      ''
    );
    Object.assign(notice.style, {
      position: 'absolute',
      left: 'clamp(12px, 3%, 28px)',
      right: 'clamp(12px, 3%, 28px)',
      bottom: 'clamp(16px, 4%, 34px)',
      zIndex: '20',
      display: 'flex',
      justifyContent: 'center',
      /**
       * Sehr wichtig:
       * Hinweis darf niemals Touch/Klick/Swipe
       * der Galerie blockieren.
       */
      pointerEvents: 'none'
    });
    const inner =
      document.createElement('div');
    inner.className =
      'wks-variant-image-notice__inner';
    Object.assign(inner.style, {
      boxSizing: 'border-box',
      width: 'min(100%, 560px)',
      padding: '13px 18px',
      background:
        'rgba(250, 247, 240, 0.97)',
      border:
        '1px solid rgba(55, 65, 55, 0.22)',
      borderRadius: '8px',
      boxShadow:
        '0 5px 22px rgba(0, 0, 0, 0.14)',
      textAlign: 'center',
      pointerEvents: 'none'
    });
    const title =
      document.createElement('p');
    title.className =
      'wks-variant-image-notice__title';
    title.textContent = NOTICE_TITLE;
    Object.assign(title.style, {
      margin: '0',
      color: '#28372b',
      fontSize: '13px',
      lineHeight: '1.45',
      fontWeight: '700',
      pointerEvents: 'none'
    });
    const text =
      document.createElement('p');
    text.className =
      'wks-variant-image-notice__text';
    text.textContent = NOTICE_TEXT;
    Object.assign(text.style, {
      margin: '4px 0 0',
      color: '#505750',
      fontSize: '12px',
      lineHeight: '1.45',
      fontWeight: '400',
      pointerEvents: 'none'
    });
    inner.appendChild(title);
    inner.appendChild(text);
    notice.appendChild(inner);
    return notice;
  };
  const attachNotice = (mediaElement) => {
    if (!mediaElement) return;
    mediaElement.style.position = 'relative';
    if (
      mediaElement.querySelector(
        '[data-wks-image-fallback-notice]'
      )
    ) {
      return;
    }
    mediaElement.appendChild(
      createNotice()
    );
  };
  /* =======================================================
     Medium auswählen
  ======================================================= */
  /**
   * Wir versuchen zuerst, Prestige über sein
   * vorhandenes Thumbnail auswählen zu lassen.
   *
   * Wir verändern dabei KEINE hidden-Werte.
   */
  const selectMedia = (
    gallery,
    mediaId
  ) => {
    if (!gallery || !mediaId) {
      return false;
    }
    const mediaElement =
      getMediaElement(gallery, mediaId);
    if (!mediaElement) {
      return false;
    }
    /*
     * OPTION 1
     * Prestige Thumbnail.
     */
    const thumbnail =
      getThumbnailElement(
        gallery,
        mediaId
      );
    if (
      thumbnail &&
      !thumbnail.hidden
    ) {
      try {
        thumbnail.click();
        return true;
      } catch (error) {
        // Weiter mit Carousel-Fallback.
      }
    }
    /*
     * OPTION 2
     * Ausschließlich horizontalen Carousel
     * verschieben.
     *
     * Kein scrollIntoView().
     */
    const carousel =
      getGalleryCarousel(gallery);
    if (!carousel) {
      return false;
    }
    try {
      const targetLeft =
        mediaElement.offsetLeft -
        carousel.offsetLeft;
      carousel.scrollTo({
        left: Math.max(0, targetLeft),
        top: 0,
        behavior: 'auto'
      });
      return true;
    } catch (error) {
      return false;
    }
  };
  /* =======================================================
     Generisches WKS-Fallbackbild
  ======================================================= */
  const createGenericFallback = (gallery) => {
    const carousel =
      getGalleryCarousel(gallery);
    if (!carousel) {
      return null;
    }
    const wrapper =
      document.createElement('div');
    wrapper.className =
      'product-gallery__media snap-center wks-generic-variant-fallback';
    wrapper.setAttribute(
      'data-wks-generic-fallback',
      ''
    );
    wrapper.setAttribute(
      'data-media-type',
      'image'
    );
    wrapper.setAttribute(
      'role',
      'group'
    );
    wrapper.setAttribute(
      'aria-label',
      'Abbildung für diese Ausführung derzeit nicht verfügbar'
    );
    Object.assign(wrapper.style, {
      position: 'relative',
      width: '100%',
      flex: '0 0 100%',
      pointerEvents: 'none'
    });
    const image =
      document.createElement('img');
    image.src =
      GENERIC_FALLBACK_IMAGE;
    image.alt =
      'Abbildung für diese Ausführung derzeit nicht verfügbar';
    image.loading = 'eager';
    image.decoding = 'async';
    image.className =
      'wks-generic-variant-fallback__image';
    Object.assign(image.style, {
      display: 'block',
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      pointerEvents: 'none'
    });
    wrapper.appendChild(image);
    carousel.prepend(wrapper);
    return wrapper;
  };
  const showGenericFallback = (gallery) => {
    if (!gallery) return;
    resetWksState(gallery);
    const genericFallback =
      createGenericFallback(gallery);
    if (!genericFallback) {
      return;
    }
    gallery.setAttribute(
      'data-wks-fallback-active',
      'true'
    );
    gallery.setAttribute(
      'data-wks-fallback-type',
      'generic'
    );
    const carousel =
      getGalleryCarousel(gallery);
    if (carousel) {
      try {
        carousel.scrollTo({
          left: 0,
          top: 0,
          behavior: 'auto'
        });
      } catch (error) {
        // Keine weitere Aktion.
      }
    }
  };
  /* =======================================================
     Gewählte Ausführung besitzt Bild
  ======================================================= */
    /* =======================================================
     Produkt besitzt nur ein Bild
  ======================================================= */
  const showSingleProductMedia = (gallery, media) => {
    if (!gallery || !media || !media.id) return;
    resetWksState(gallery);
    selectMedia(gallery, String(media.id));
  };
const showExecutionMedia = (
    gallery,
    executionVariant
  ) => {
    if (
      !gallery ||
      !hasOwnMedia(executionVariant)
    ) {
      return;
    }
    resetWksState(gallery);
    const mediaId =
      String(
        executionVariant.featuredMedia.id
      );
    selectMedia(
      gallery,
      mediaId
    );
  };
  /* =======================================================
     Ersatzbild Natur/Bemalt
  ======================================================= */
  const showVariantFallback = (
    gallery,
    fallbackVariant
  ) => {
    if (
      !gallery ||
      !hasOwnMedia(fallbackVariant)
    ) {
      return;
    }
    resetWksState(gallery);
    const mediaId =
      String(
        fallbackVariant.featuredMedia.id
      );
    const mediaElement =
      getMediaElement(
        gallery,
        mediaId
      );
    /**
     * Wir machen versteckte Medien NICHT mehr
     * manuell sichtbar.
     *
     * Ist das Medium im aktuellen Prestige-DOM
     * nicht verwendbar, greifen wir auf das
     * generische Fallback zurück.
     */
    if (
      !mediaElement ||
      mediaElement.hidden
    ) {
      showGenericFallback(gallery);
      return;
    }
    gallery.setAttribute(
      'data-wks-fallback-active',
      'true'
    );
    gallery.setAttribute(
      'data-wks-fallback-type',
      'variant'
    );
    gallery.setAttribute(
      'data-wks-fallback-media-id',
      mediaId
    );
    attachNotice(mediaElement);
    selectMedia(
      gallery,
      mediaId
    );
  };
  /* =======================================================
     Hauptlogik
  ======================================================= */
    /* WKS 1.4: Einzelbild direkt aus der echten Prestige-Galerie erkennen. */
  const getSingleRealGalleryMedia = (gallery) => {
    if (!gallery) return null;
    const media = Array.from(
      gallery.querySelectorAll('.product-gallery__media[data-media-id]')
    ).filter((element) => {
      return (
        !element.hasAttribute('data-wks-generic-fallback') &&
        !element.classList.contains('wks-generic-variant-fallback')
      );
    });
    return media.length === 1 ? media[0] : null;
  };
  const useSingleRealGalleryMedia = (gallery) => {
    const mediaElement = getSingleRealGalleryMedia(gallery);
    if (!mediaElement) return false;
    resetWksState(gallery);
    const mediaId = mediaElement.getAttribute('data-media-id');
    if (mediaId) {
      selectMedia(gallery, String(mediaId));
    }
    return true;
  };
const applyFallback = (config) => {
    if (
      !config ||
      !document.documentElement.contains(config)
    ) {
      return;
    }
    const data =
      getVariantData(config);
    const gallery =
      getGallery(config);
    if (!data || !gallery) {
      return;
    }
    /*
     * FALL 0 – GENAU EIN ECHTES PRODUKTBILD IN DER GALERIE
     * Dieses Bild gilt als allgemeines Produktbild.
     * Varianten-/Ausführungs-Fallback wird vollständig übersprungen.
     */
    // WKS 1.5: Nur explizit markierte Serien mit genau einem,
    // keiner Variante zugewiesenen Bemaltbild erhalten den Hinweis.
    const singleMedia = getSingleRealGalleryMedia(gallery);
    if (singleMedia) {
      const singlePainted = config.dataset.wksSingleImagePainted === 'true';
      const unassigned = data.variants.every(variant => !hasOwnMedia(variant));
      const current = getCurrentVariant(config, data);
      const execution = normalize(getExecutionValue(current, data));
      if (singlePainted && unassigned && current && execution && execution !== 'bemalt') {
        resetWksState(gallery);
        const mediaId = singleMedia.getAttribute('data-media-id');
        if (mediaId) selectMedia(gallery, mediaId);
        attachNotice(singleMedia);
        gallery.setAttribute('data-wks-fallback-active', 'true');
        gallery.setAttribute('data-wks-fallback-type', 'single-painted');
        return;
      }
      useSingleRealGalleryMedia(gallery);
      return;
    }
const currentVariant =
      getCurrentVariant(
        config,
        data
      );
    if (!currentVariant) {
      resetWksState(gallery);
      return;
    }
    /*
     * AUSFÜHRUNG ERMITTELN
     *
     * Größe ist irrelevant.
     */
    const singleProductMedia = null; // WKS 1.4: alte 1.3-Prüfung deaktiviert
    /*
     * FALL 0 – EINZELBILD
     * Nur ein einziges echtes Produktbild:
     * -> immer dieses Bild anzeigen
     * -> Größe und Ausführung ignorieren
     * -> kein Hinweis / kein WKS-Fallback
     */
    if (singleProductMedia) {
      showSingleProductMedia(gallery, singleProductMedia);
      return;
    }
    const executionVariant =
      findExecutionVariant(
        data,
        currentVariant
      );
    /*
     * FALL 1
     *
     * Für die gewählte Ausführung existiert
     * irgendwo ein Bild.
     *
     * -> Bild verwenden.
     * -> Kein Hinweis.
     */
    if (executionVariant) {
      showExecutionMedia(
        gallery,
        executionVariant
      );
      return;
    }
    /*
     * FALL 2
     *
     * Für die gewählte Ausführung existiert
     * überhaupt kein Bild.
     *
     * -> Natur/Bemalt Ersatzbild.
     * -> Hinweis anzeigen.
     */
    const fallbackVariant =
      findFallbackVariant(data);
    if (fallbackVariant) {
      showVariantFallback(
        gallery,
        fallbackVariant
      );
      return;
    }
    /*
     * FALL 3
     *
     * Auch kein Ersatzbild vorhanden.
     *
     * -> Generisches WKS Bild.
     */
    showGenericFallback(gallery);
  };
  /* =======================================================
     Zeitsteuerung
  ======================================================= */
  const scheduleApply = (
    config,
    delay = 100
  ) => {
    if (!config) return;
    window.clearTimeout(
      config.__wksImageFallbackTimer
    );
    config.__wksImageFallbackTimer =
      window.setTimeout(() => {
        applyFallback(config);
      }, delay);
  };
  /* =======================================================
     Events
  ======================================================= */
  const bindConfig = (config) => {
    if (
      !config ||
      config.dataset
        .wksImageFallbackBound === 'true'
    ) {
      return;
    }
    config.dataset.wksImageFallbackBound =
      'true';
    const formId =
      config.dataset.wksFormId;
    const form =
      getForm(formId);
    /*
     * Wichtig:
     *
     * Wir hören auf Änderungen am Produktformular,
     * greifen aber NICHT in das Event ein.
     *
     * Kein preventDefault.
     * Kein stopPropagation.
     * Kein stopImmediatePropagation.
     */
    if (form) {
      form.addEventListener(
        'change',
        () => {
          /**
           * Prestige bekommt zuerst Zeit,
           * seine eigene Varianten-/Galerielogik
           * vollständig auszuführen.
           */
          scheduleApply(
            config,
            180
          );
        }
      );
    }
    /*
     * Picker Observer
     *
     * Nur den Variant Picker beobachten,
     * NICHT document.body.
     *
     * Dadurch reagieren wir weiterhin auf
     * Prestiges Partial Updates, ohne bei jeder
     * Galerieänderung eine neue Kaskade auszulösen.
     */
    const picker =
      getVariantPicker(config);
    if (picker) {
      const pickerObserver =
        new MutationObserver(() => {
          scheduleApply(
            config,
            120
          );
        });
      pickerObserver.observe(
        picker,
        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
      config.__wksPickerObserver =
        pickerObserver;
    }
    /*
     * Initial einmal ausführen.
     */
    scheduleApply(
      config,
      150
    );
  };
  /* =======================================================
     Initialisierung
  ======================================================= */
  const init = () => {
    getConfigElements().forEach(
      bindConfig
    );
  };
  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
  /*
   * Shopify Theme Editor / dynamisch neu
   * geladene Section.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {
      init();
    }
  );
  /* =======================================================
     Debug / manuelles Refresh
  ======================================================= */
  window.WKSVariantImageFallback = {
    version: VERSION,
    refresh: init
  };
})();
